import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_saleshustler/artifacts/lbev3bte_Gemini_Generated_Image_r450fmr450fmr450.png";

export default function DirectAccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    // Verify token on load
    const verifyToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-token/${token}`);
        setTokenValid(response.data.valid);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleAccess = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/direct-access`, 
        { token, email },
        { withCredentials: true }
      );

      const data = response.data;
      toast.success(`Welcome, ${data.name}!`);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/dashboard', { state: { user: data } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Access failed';
      if (message.includes("not approved")) {
        toast.error("Your email is not on the approved list. Please contact your admin.", { duration: 6000 });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 hero-gradient">
        <div className="animate-pulse-glow p-8 rounded-full">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 hero-gradient">
        <Card className="w-full max-w-md glass-panel border-white/10">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">Invalid Access Link</CardTitle>
            <CardDescription className="text-muted-foreground">
              This access link is invalid or has expired. Please contact your admin for a new link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-primary text-primary-foreground"
            >
              Go to Login Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 hero-gradient">
      <Card className="w-full max-w-md glass-panel border-white/10" data-testid="direct-access-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={LOGO_URL} alt="IntegrityGO" className="h-16 w-auto" />
          </div>
          <div className="flex justify-center">
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">
              <CheckCircle className="h-4 w-4" />
              Direct Access
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">Welcome to 20x5</CardTitle>
          <CardDescription className="text-muted-foreground">
            Enter your approved email to access your sales tracker
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleAccess} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Your Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-background/50 border-white/10 focus:border-primary"
                  required
                  autoFocus
                  data-testid="direct-email-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the email that was approved by your admin
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base bg-secondary text-secondary-foreground hover:bg-secondary/90"
              disabled={loading || !email.trim()}
              data-testid="direct-access-btn"
            >
              {loading ? (
                "Accessing..."
              ) : (
                <>
                  Access Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
